import { useMemo, useCallback, memo } from 'react';
import { useIntl } from 'react-intl';

// MUI
import Box from '@mui/material/Box';

// common
import CheckBoxSchema from 'common/SchemaForm/element/checkbox/CheckBoxSchema';

// screen
import type {
  CurrentSelectedRole,
  RolesToAssign,
  Client,
  UtilFunctions,
} from 'modules/account/types';
import {
  countCustomers,
  findByKey,
  findByOrgIdAndRoleId,
} from 'modules/account/detail/helpers/utils';

interface CheckboxProps {
  customer: Client;
  isChecked: boolean;
  isDisabled: boolean;
  onToggle: (customer: Client, checked: boolean) => void;
}

// Memoized CustomerCheckbox component with optimized props
const CustomerCheckbox = memo((props: CheckboxProps) => {
  const { customer, isChecked, isDisabled, onToggle } = props;

  return (
    <Box>
      <CheckBoxSchema
        label={customer.name}
        checked={isChecked}
        disabled={isDisabled}
        onChange={(_e, checked) => onToggle(customer, checked)}
      />
    </Box>
  );
});

interface Props {
  utilFunctions: UtilFunctions;
  rolesToAssign: RolesToAssign;
  customers: Client[];
  currentSelectedRole: CurrentSelectedRole;
  isCQQL: boolean;
  viewOnly?: boolean;
}

const FullCustomersList = (props: Props) => {
  const {
    utilFunctions: { selectACustomer, selectAllCustomer },
    currentSelectedRole,
    rolesToAssign,
    isCQQL,
    viewOnly,
    customers,
  } = props;

  const { forClientBusiness, forGovAgencyBusiness, forAdminRoles, forSystemRoles } =
    rolesToAssign.rolesAssignment;

  const { formatMessage } = useIntl();

  /**
   * Đếm số lượng khách hàng của role đang được chọn,
   * dựa vào rolesType và role của currentSelectedRole trong rolesToAssign.rolesAssignment
   */
  const customersCount = useMemo(() => {
    if (!currentSelectedRole) return 0;

    const { rolesType, role } = currentSelectedRole;
    const isBothBusinessRoleAndCQQL = rolesType === 'businessRoles' && isCQQL;
    const isRegularBusinessRole = rolesType === 'businessRoles' && !isCQQL;

    if (isRegularBusinessRole) {
      return countCustomers(forClientBusiness, role.id);
    }

    if (isBothBusinessRoleAndCQQL) {
      const roleToEdit = findByKey(forGovAgencyBusiness, role.id);
      if (!roleToEdit) return 0;
      const count = (roleToEdit.includedClientIds ?? []).length;
      return count === 0 ? customers.length : count;
    }

    // rolesType === 'adminRoles'
    return countCustomers(forAdminRoles, role.id);
  }, [
    currentSelectedRole,
    isCQQL,
    forAdminRoles,
    forClientBusiness,
    forGovAgencyBusiness,
    customers.length,
  ]);

  const shouldCheckCqql = useCallback(
    (roleId: string, clientId: string) => {
      const cqql = findByKey(forGovAgencyBusiness, roleId);
      if (!cqql) return false;
      const cqqlClientIds = cqql.includedClientIds || [];

      // mảng rỗng, check tất cả khách hàng (theo quận của CQQL)
      if (!cqqlClientIds.length) return true;

      // mảng có phần tử, check khách hàng cụ thể
      return cqqlClientIds.includes(clientId);
    },
    [forGovAgencyBusiness]
  );

  // Memoize business role checker
  const checkBusinessRole = useCallback(
    (operationCode: string, roleId: string) => {
      if (isCQQL) return shouldCheckCqql(roleId, operationCode);
      return !!findByOrgIdAndRoleId(forClientBusiness, operationCode, roleId);
    },
    [isCQQL, shouldCheckCqql, forClientBusiness]
  );

  // Memoize admin role checker
  const checkAdminRole = useCallback(
    (opCode: string, roleId: string) => !!findByOrgIdAndRoleId(forAdminRoles, opCode, roleId),
    [forAdminRoles]
  );

  // Memoize system role checker
  const checkSystemRole = useCallback(
    (opCode: string, roleId: string) => !!findByOrgIdAndRoleId(forSystemRoles, opCode, roleId),
    [forSystemRoles]
  );

  // Extract current role info to reduce shouldCheck dependencies
  const currentRoleInfo = useMemo(() => {
    if (!currentSelectedRole) return null;
    return {
      rolesType: currentSelectedRole.rolesType,
      roleId: currentSelectedRole.role.id,
    };
  }, [currentSelectedRole]);

  /**
   * Hiển thị trạng thái checkbox của khách hàng
   */
  const shouldCheck = useCallback(
    ({ operationCode }: Client) => {
      if (!currentRoleInfo) return false;

      const { rolesType, roleId } = currentRoleInfo;

      switch (rolesType) {
        case 'businessRoles':
          return checkBusinessRole(operationCode, roleId);
        case 'adminRoles':
          return checkAdminRole(operationCode, roleId);
        case 'systemRoles':
          return checkSystemRole(operationCode, roleId);
        default:
          return false;
      }
    },
    [currentRoleInfo, checkBusinessRole, checkAdminRole, checkSystemRole]
  );

  // Memoize select all checkbox props
  const selectAllCheckboxProps = useMemo(() => {
    if (customers.length <= 1) return null;

    return {
      label: formatMessage({ id: 'all' }),
      onChange: (_e: any, isChecked: boolean) => selectAllCustomer(isChecked),
      disabled: viewOnly,
      checked: customersCount === customers.length,
    };
  }, [customers.length, formatMessage, selectAllCustomer, viewOnly, customersCount]);

  // Memoize customer check results to avoid recalculating for each render
  const customerCheckResults = useMemo(() => {
    if (!currentRoleInfo) return new Map();

    const results = new Map<string, boolean>();
    customers.forEach((customer) => {
      results.set(customer.operationCode, shouldCheck(customer));
    });
    return results;
  }, [customers, currentRoleInfo, shouldCheck]);

  // Early return if no customers (after all hooks)
  if (!customers.length) {
    return null;
  }

  return (
    <>
      {selectAllCheckboxProps && <CheckBoxSchema {...selectAllCheckboxProps} />}

      <Box sx={{ marginTop: '12px' }}>
        {customers.map((customer, index) => (
          <CustomerCheckbox
            key={customer.operationCode || index}
            customer={customer}
            isChecked={customerCheckResults.get(customer.operationCode) || false}
            isDisabled={Boolean(viewOnly)}
            onToggle={selectACustomer}
          />
        ))}
      </Box>
    </>
  );
};

export default FullCustomersList;
